import {GridColDef, GridSortCellParams} from '@mui/x-data-grid';
import AurSvgIcon from '../common/aur-svg-icon/AurSvgIcon';
import {IBulkUserTableHeaders} from '../../components/pages/user-management/models';
import {t} from 'i18next';
import _ from 'lodash';

interface IUserDetailsRow {
  firstName: string;
  lastName: string;
  email: string;
}

function getFullName(row: IUserDetailsRow | undefined) {
  if (!row) return t('common.editDetails.unknown');
  const {firstName = '', lastName = ''} = row;

  if (firstName && lastName) {
    return `${lastName}, ${firstName}`.trim();
  }

  return firstName || lastName;
}

export const BulkUserResultTableColumns = (): GridColDef[] => [
  {
    field: IBulkUserTableHeaders.name,
    headerName: t('bulkUsers.table.nameHeader'),
    width: 180,
    sortable: true,
    renderCell: (params) => {
      const row = params.row as IUserDetailsRow;
      return !_.isEmpty(getFullName(row)) ? (
        <div className="cell-truncate">{getFullName(row)}</div>
      ) : null;
    },
    sortComparator: (
      v1: string,
      v2: string,
      cellParams1: GridSortCellParams,
      cellParams2: GridSortCellParams
    ) => {
      const row1 = cellParams1.api.getRow(cellParams1.id) as
        | IUserDetailsRow
        | undefined;
      const row2 = cellParams2.api.getRow(cellParams2.id) as
        | IUserDetailsRow
        | undefined;

      const fullNameA = getFullName(row1);
      const fullNameB = getFullName(row2);

      const [lastNameA, firstNameA] = fullNameA
        .split(',')
        .map((name) => name.trim().toLowerCase());
      const [lastNameB, firstNameB] = fullNameB
        .split(',')
        .map((name) => name.trim().toLowerCase());

      const lastNameComparison = lastNameA.localeCompare(lastNameB);
      if (lastNameComparison !== 0) return lastNameComparison;

      return firstNameA.localeCompare(firstNameB);
    },
  },
  {
    field: IBulkUserTableHeaders.email,
    headerName: t('bulkUsers.table.emailHeader'),
    width: 180,
    sortable: true,
  },
  {
    field: IBulkUserTableHeaders.role,
    headerName: t('bulkUsers.table.roleHeader'),
    width: 120,
    sortable: false,
    renderCell: (params) => {
      return !_.isEmpty(params?.row?.userRole?.name) ? (
        <div className="cell-truncate">{params?.row?.userRole?.name}</div>
      ) : null;
    },
  },
  {
    field: IBulkUserTableHeaders.ipLimitAddress,
    headerName: t('bulkUsers.table.limitIpAddressHeader'),
    width: 180,
    sortable: false,
    renderCell: (params) => {
      return !_.isEmpty(params?.row?.limitIpAddress) ? (
        <div className="cell-truncate">{params?.row?.limitIpAddress}</div>
      ) : null;
    },
  },
  {
    field: IBulkUserTableHeaders.clientPermissionGroup,
    headerName: t('bulkUsers.table.clientpermissionGroupHeader'),
    width: 180,
    headerClassName: 'wrap-header',
    sortable: false,
    renderCell: (params) => {
      return !_.isEmpty(
        params?.row?.clientPermissionGroup[0]?.permissionGroupName
      ) ? (
        <div className="cell-truncate" tabIndex={0}>
          {params?.row?.clientPermissionGroup[0]?.permissionGroupName}
        </div>
      ) : null;
    },
  },
  {
    field: IBulkUserTableHeaders.projectGroup,
    headerName: t('bulkUsers.table.projectGroupHeader'),
    width: 150,
    sortable: false,
    renderCell: (params) => {
      return !_.isEmpty(params?.row?.projects[0]?.projectName) ? (
        <div className="cell-truncate" tabIndex={0}>
          {params?.row?.projects[0]?.projectName}
        </div>
      ) : null;
    },
  },
  {
    field: IBulkUserTableHeaders.projectPermissionGroup,
    headerName: t('bulkUsers.table.projectPermissionGroupHeader'),
    width: 180,
    headerClassName: 'wrap-header',
    sortable: false,
    renderCell: (params) => {
      return !_.isEmpty(
        params?.row?.projectPermissionGroup[1]?.permissionGroupName ??
          params?.row?.projectPermissionGroup[0]?.permissionGroupName
      ) ? (
        <div className="cell-truncate" tabIndex={0}>
          {params?.row?.projectPermissionGroup[1]?.permissionGroupName ??
            params?.row?.projectPermissionGroup[0]?.permissionGroupName}
        </div>
      ) : null;
    },
  },
  {
    field: IBulkUserTableHeaders.workspace,
    headerName: t('bulkUsers.table.workspaceHeader'),
    width: 120,
    sortable: false,
    renderCell: (params) => {
      return !_.isEmpty(
        params?.row?.projects[0]?.workspaces[0]?.workspaceName
      ) ? (
        <div className="cell-truncate" tabIndex={0}>
          {params?.row?.projects[0]?.workspaces[0]?.workspaceName}
        </div>
      ) : null;
    },
  },
  {
    field: IBulkUserTableHeaders.editIcon,
    headerName: t('common.editDetails.editIcon'),
    renderHeader: () => (
      <div style={{display: 'none'}}>{t('common.editDetails.editIcon')}</div>
    ),
    width: 40,
    sortable: false,
    headerClassName: 'no-separator',
    renderCell: () => (
      <div
        tabIndex={0}
        className="edit-icon"
        role="button"
        aria-label={t('common.editDetails.editIconHint')}
      >
        <AurSvgIcon src="images/edit_pen_icon.svg" />
      </div>
    ),
  },
];
